'use strict';

// @see https://data.geopf.fr/geocodage/openapi
const geocodingApiURL = 'https://data.geopf.fr/geocodage';
const mapboxToken = 'pk.eyJ1IjoicmlnZXRoZW8iLCJhIjoiY210aW8xbDF1MDNhNDJ3cjV6cXl6cjFscyJ9.E1iaZTcLCrJIKmf8rHazrA';

// Mirrors the --card-color values set per `.kcjo-offer-card--{category}` in search.scss.
const categoryColors = {
	asv: '#dba392',
	veterinaire: '#c7c6f2',
	etudiants: '#f5d093',
	support: '#13a4b8'
};
const defaultCategoryColor = '#11b2bd';

const filtersForm = document.querySelector('.kcjo-filters');

const locationInput = document.getElementById('location');
const locationButton = document.querySelector('.kcjo-location-input button');
const locationSuggestionsContainer = document.getElementById('location-suggestions');

const offersViewToggleButtons = document.querySelectorAll('.kcjo-offers-view-toggle button');

const offersMap = document.getElementById('map');
const offersContent = document.querySelector('.kcjo-offers__content');
const offersCount = document.querySelector('.kcjo-offers-count');

const loadMoreButton = document.getElementById('load-more');

if (filtersForm) {
	filtersForm.querySelectorAll('select').forEach((select) => {
		const updateSelectValidity = () => select.classList.toggle('is-valid', select.value !== '');

		updateSelectValidity();
		select.addEventListener('change', updateSelectValidity);
	});
}

let map;
let mapInitialized = false;
let currentMarkers = parseInitialMarkers();

function parseInitialMarkers() {
	const markersScript = document.getElementById('offers-markers');

	if (!markersScript) return [];

	try {
		return JSON.parse(markersScript.textContent) || [];
	} catch (error) {
		return [];
	}
}

// Offers sharing the exact same coordinates (eg. same office address) would otherwise stack on a
// single pixel and hide each other once zoomed past the cluster's clusterMaxZoom, so markers within
// a group are fanned out a few meters around the real spot, "spiderfy" style, keeping each one its
// own clickable point with its own popup instead of merging them.
const SPIDERFY_LEG_METERS = 18;
const METERS_PER_DEGREE_LAT = 111320;

function groupMarkersByCoordinates(markers) {
	const groups = new Map();

	markers.forEach((marker) => {
		const key = `${marker.longitude},${marker.latitude}`;
		const group = groups.get(key);

		if (group) {
			group.push(marker);
		} else {
			groups.set(key, [marker]);
		}
	});

	return Array.from(groups.values());
}

function offsetCoordinates(longitude, latitude, distanceMeters, angleRadians) {
	const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos((latitude * Math.PI) / 180);

	return [
		longitude + (distanceMeters * Math.cos(angleRadians)) / metersPerDegreeLng,
		latitude + (distanceMeters * Math.sin(angleRadians)) / METERS_PER_DEGREE_LAT
	];
}

function markersToGeoJSON(markers) {
	const features = [];

	groupMarkersByCoordinates(markers).forEach((group) => {
		group.forEach((marker, index) => {
			const coordinates =
				group.length === 1
					? [marker.longitude, marker.latitude]
					: offsetCoordinates(marker.longitude, marker.latitude, SPIDERFY_LEG_METERS, (2 * Math.PI * index) / group.length);

			features.push({
				type: 'Feature',
				id: marker.id,
				geometry: { type: 'Point', coordinates },
				properties: {
					id: marker.id,
					html: marker.html,
					color: categoryColors[marker.category] || defaultCategoryColor
				}
			});
		});
	});

	return { type: 'FeatureCollection', features };
}

function getMarkersBounds(markers) {
	const bounds = new mapboxgl.LngLatBounds();

	markers.forEach((marker) => bounds.extend([marker.longitude, marker.latitude]));

	return bounds;
}

// Testing: unclustered points get a plain mapboxgl.Marker pin (same as the offer page's clinic
// map) instead of a circle layer; clusters stay a GL circle layer. Mirrors Mapbox's cluster-html
// pattern: markersOnScreen is diffed against the source's currently rendered unclustered features
// on every 'render', since that's the only way to know which points survived clustering at the
// current zoom.
const markersOnScreen = {};

// Mirrors the mobile breakpoint in search.scss.
const mobileMediaQuery = window.matchMedia('(max-width: 768px)');

// On mobile the map is too small for Mapbox's auto anchoring to keep the popup inside the
// container, so the popup is pinned above its marker and the map pans to center it. The anchor
// is fixed so it can't flip mid-pan and move the popup away from where we centered it.
function createMarkerPopup(html) {
	if (!mobileMediaQuery.matches) {
		return new mapboxgl.Popup().setHTML(html);
	}

	const popup = new mapboxgl.Popup({ anchor: 'bottom' }).setHTML(html);

	popup.on('open', () => {
		const popupRect = popup.getElement().getBoundingClientRect();
		const mapRect = map.getContainer().getBoundingClientRect();

		map.panBy([
			popupRect.left + popupRect.width / 2 - (mapRect.left + mapRect.width / 2),
			popupRect.top + popupRect.height / 2 - (mapRect.top + mapRect.height / 2)
		]);
	});

	return popup;
}

function updateUnclusteredMarkers() {
	const source = map.getSource('offers');

	if (!source || !map.isSourceLoaded('offers')) return;

	const features = map.querySourceFeatures('offers', { filter: ['!', ['has', 'point_count']] });
	const idsOnScreen = new Set();

	features.forEach((feature) => {
		const id = String(feature.properties.id);
		idsOnScreen.add(id);

		if (markersOnScreen[id]) return;

		const popup = createMarkerPopup(feature.properties.html);

		markersOnScreen[id] = new mapboxgl.Marker({ color: feature.properties.color })
			.setLngLat(feature.geometry.coordinates)
			.setPopup(popup)
			.addTo(map);
	});

	Object.keys(markersOnScreen).forEach((id) => {
		if (idsOnScreen.has(id)) return;

		markersOnScreen[id].remove();
		delete markersOnScreen[id];
	});
}

function updateMapMarkers(markers) {
	currentMarkers = markers;

	const source = map && map.getSource('offers');

	if (!source) return;

	source.setData(markersToGeoJSON(markers));

	if (markers.length > 0) {
		map.fitBounds(getMarkersBounds(markers), { padding: 50, maxZoom: 14 });
	}
}

function initMap() {
	mapInitialized = true;

	map = new mapboxgl.Map({
		accessToken: mapboxToken,
		container: offersMap,
		center: [2.2137, 46.2276],
		zoom: 5,
		minZoom: 4,
		projection: 'mercator',
		language: 'fr'
	});

	map.on('load', () => {
		map.addSource('offers', {
			type: 'geojson',
			data: markersToGeoJSON(currentMarkers),
			cluster: true,
			clusterMaxZoom: 14,
			clusterRadius: 50
		});

		map.addLayer({
			id: 'clusters',
			type: 'circle',
			source: 'offers',
			filter: ['has', 'point_count'],
			paint: {
				'circle-color': '#ffffff',
				'circle-radius': ['step', ['get', 'point_count'], 18, 10, 22, 25, 28]
			}
		});

		map.addLayer({
			id: 'cluster-count',
			type: 'symbol',
			source: 'offers',
			filter: ['has', 'point_count'],
			paint: {
				'text-color': '#11b2bd'
			},
			layout: {
				'text-field': ['get', 'point_count_abbreviated'],
				'text-font': ['Poppins', 'Arial Unicode MS Bold'],
				'text-size': 14
			}
		});

		map.on('click', 'clusters', (event) => {
			const features = map.queryRenderedFeatures(event.point, { layers: ['clusters'] });
			const clusterId = features[0].properties.cluster_id;

			map.getSource('offers').getClusterExpansionZoom(clusterId, (error, zoom) => {
				if (error) return;

				map.easeTo({ center: features[0].geometry.coordinates, zoom });
			});
		});

		map.on('mouseenter', 'clusters', () => {
			map.getCanvas().style.cursor = 'pointer';
		});
		map.on('mouseleave', 'clusters', () => {
			map.getCanvas().style.cursor = '';
		});

		map.on('render', updateUnclusteredMarkers);

		if (currentMarkers.length > 0) {
			map.fitBounds(getMarkersBounds(currentMarkers), { padding: 50, maxZoom: 14 });
		}
	});
}

if (filtersForm && locationInput) {
	let searchTimeout;
	let currentPage = 1;

	function setLongLatHiddenFields(latitude, longitude) {
		const latitudeInput = document.getElementById('latitude');
		const longitudeInput = document.getElementById('longitude');

		if (latitudeInput) latitudeInput.value = latitude;
		if (longitudeInput) longitudeInput.value = longitude;

		locationInput.classList.toggle('is-valid', latitude !== '' && longitude !== '');
	}

	function selectLocation(label, latitude, longitude) {
		locationInput.value = label;
		setLongLatHiddenFields(latitude, longitude);
		locationSuggestionsContainer.innerHTML = '';
	}

	// The user may type a city and submit without picking a suggestion: geocode it so the radius still applies.
	async function ensureLocationCoordinates() {
		const query = locationInput.value.trim();

		if (query === '' || document.getElementById('latitude').value !== '') return;

		clearTimeout(searchTimeout);
		setLocationButtonLoading(false);

		try {
			const response = await fetch(`${geocodingApiURL}/search?type=municipality&limit=1&q=${encodeURIComponent(query)}`);
			const data = await response.json();
			const feature = data.features && data.features[0];

			if (feature) {
				selectLocation(
					`${feature.properties.city} (${feature.properties.postcode})`,
					feature.geometry.coordinates[1],
					feature.geometry.coordinates[0]
				);
			}
		} catch (error) {
			// Geocoding unavailable: search without location rather than blocking the form.
		}
	}

	async function fetchOffers(page) {
		const body = new FormData(filtersForm);
		body.append('action', 'get_offers');
		body.append('page', page);

		const response = await fetch(filtersForm.action, { method: 'POST', body });

		return response.json();
	}

	function updateOffersCount(total) {
		if (offersCount) offersCount.textContent = `${total} offre${total > 1 ? 's' : ''}`;
	}

	function updateLoadMoreButton(hasMore) {
		if (loadMoreButton) loadMoreButton.hidden = !hasMore;
	}

	function setLoadMoreButtonLoading(isLoading) {
		if (!loadMoreButton) return;

		loadMoreButton.classList.toggle('is-loading', isLoading);
		loadMoreButton.disabled = isLoading;
	}

	function scrollToFirstOffer() {
		const firstOfferCard = offersContent && offersContent.querySelector('.kcjo-offer-card');

		// offsetParent is null when the card (or an ancestor) is hidden, e.g. the map view is active.
		if (!firstOfferCard || !firstOfferCard.offsetParent) return;

		// The offset itself is set via `scroll-margin-top` on .kcjo-offer-card in search.scss.
		firstOfferCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	function setLocationButtonLoading(isLoading) {
		if (!locationButton) return;

		locationButton.classList.toggle('is-loading', isLoading);
		locationButton.disabled = isLoading;
	}

	locationInput.addEventListener('input', (event) => {
		const query = event.target.value;

		clearTimeout(searchTimeout);
		setLongLatHiddenFields('', '');

		if (query.length < 3) {
			setLocationButtonLoading(false);
			locationSuggestionsContainer.innerHTML = '';
			return;
		}

		setLocationButtonLoading(true);

		searchTimeout = setTimeout(async () => {
			try {
				const response = await fetch(`${geocodingApiURL}/search?type=municipality&limit=5&q=${encodeURIComponent(query)}`);
				const data = await response.json();

				locationSuggestionsContainer.innerHTML = '';
				const suggestionsFragment = document.createDocumentFragment();

				data.features.forEach((feature) => {
					const suggestion = document.createElement('div');
					suggestion.classList.add('location-suggestions-item');
					suggestion.dataset.lat = feature.geometry.coordinates[1];
					suggestion.dataset.lon = feature.geometry.coordinates[0];
					suggestion.textContent = `${feature.properties.city} (${feature.properties.postcode})`;

					// Prevents the input from blurring (and the suggestions from closing) before the click is handled.
					suggestion.addEventListener('mousedown', (event) => event.preventDefault());

					suggestion.addEventListener('click', () => {
						selectLocation(suggestion.textContent, suggestion.dataset.lat, suggestion.dataset.lon);
					});

					suggestionsFragment.appendChild(suggestion);
				});

				locationSuggestionsContainer.appendChild(suggestionsFragment);
			} finally {
				setLocationButtonLoading(false);
			}
		}, 300);
	});

	if (locationButton) {
		locationButton.addEventListener('click', () => {
			if (!navigator.geolocation) return;

			setLocationButtonLoading(true);

			navigator.geolocation.getCurrentPosition(
				async (position) => {
					const { latitude, longitude } = position.coords;

					setLongLatHiddenFields(latitude, longitude);

					try {
						const response = await fetch(`${geocodingApiURL}/reverse?lat=${latitude}&lon=${longitude}`);
						const data = await response.json();

						if (data.features && data.features.length > 0) {
							const feature = data.features[0];
							selectLocation(`${feature.properties.city} (${feature.properties.postcode})`, latitude, longitude);
						}
					} finally {
						setLocationButtonLoading(false);
					}
				},
				() => {
					setLocationButtonLoading(false);
				}
			);
		});
	}

	filtersForm.addEventListener('reset', () => {
		// Native reset restores field values without firing input/change events, so re-run validity checks after it completes.
		setTimeout(() => {
			setLongLatHiddenFields('', '');
			locationSuggestionsContainer.innerHTML = '';
			filtersForm.querySelectorAll('select').forEach((select) => select.classList.toggle('is-valid', select.value !== ''));
		}, 0);
	});

	filtersForm.addEventListener('submit', async (event) => {
		event.preventDefault();

		await ensureLocationCoordinates();

		currentPage = 1;
		const result = await fetchOffers(currentPage);

		if (result.success) {
			if (offersContent) offersContent.innerHTML = result.data.html;
			updateOffersCount(result.data.total);
			updateLoadMoreButton(result.data.hasMore);
			updateMapMarkers(result.data.markers);
			scrollToFirstOffer();
		}
	});

	if (loadMoreButton) {
		loadMoreButton.addEventListener('click', async () => {
			setLoadMoreButtonLoading(true);

			const result = await fetchOffers(currentPage + 1);

			if (result.success) {
				currentPage += 1;
				if (offersContent) offersContent.insertAdjacentHTML('beforeend', result.data.html);
				updateLoadMoreButton(result.data.hasMore);
			}

			setLoadMoreButtonLoading(false);
		});
	}
}

if (offersViewToggleButtons.length > 0 && offersContent && offersMap) {
	offersViewToggleButtons.forEach((button) => {
		button.addEventListener('click', () => {
			offersViewToggleButtons.forEach((btn) => btn.toggleAttribute('aria-current'));

			if (button.dataset.view === 'map') {
				offersContent.style.display = 'none';
				offersMap.style.display = 'block';

				if (!mapInitialized) {
					initMap();
				} else {
					map.resize();
				}
			} else {
				offersContent.style.display = 'grid';
				offersMap.style.display = 'none';
			}
		});
	});
}
